import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ShiftReportRow } from '@order-pilot/shared';
import { AsyncSection } from '@/components/AsyncSection';
import { Badge } from '@/components/Badge';
import { IconClock } from '@/components/Icons';
import { PageHeader } from '@/components/PageHeader';
import {
  ReportColumnsButton,
  ReportFiltersButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { SectionCard } from '@/components/SectionCard';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { ExportMenu } from '@/features/portal/ExportMenu';
import { usePortal } from '@/features/portal/PortalContext';
import { humanise } from '@/features/portal/badges';
import { fetchShifts, portalKeys } from '@/features/portal/api';
import { toReportRange } from '@/features/portal/filters';
import { applyClientFilters } from '@/features/reports/applyClientFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { useReportToolbar } from '@/features/reports/useReportToolbar';
import { amountSign, formatCount, formatMoney, formatPosDateTime } from '@/lib/format';

function StatusBadge({ status }: { status: string }) {
  const open = status.toLowerCase().includes('open');
  return (
    <Badge tone={open ? 'accent' : 'neutral'} dot>
      {status ? humanise(status) : 'Closed'}
    </Badge>
  );
}

/** Variance is the number the owner actually reconciles — never let it read as neutral. */
function Variance({ value, currency }: { value: string | null; currency: string }) {
  if (value === null) {
    return <span className="text-xs text-slate-400">Shift open</span>;
  }
  const sign = amountSign(value);
  if (sign === 0) {
    return <span className="tabular-nums text-slate-500">{formatMoney(value, currency)}</span>;
  }
  return (
    <span className={`font-semibold tabular-nums ${sign < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>
      {formatMoney(value, currency)}
      <span className="ml-1 text-xs font-medium">{sign < 0 ? 'short' : 'over'}</span>
    </span>
  );
}

const FILTER_FIELDS = ['Opened by', 'Status'] as const;

const TOGGLEABLE = [
  'closed', 'status', 'openingCash', 'cardSales', 'creditSales', 'totalOrders', 'cashExpenses', 'cashPurchases',
  'expectedCash', 'actualCash', 'kotSent', 'kotVoided',
] as const;

const COLUMN_LABELS: Record<string, string> = {
  closed: 'Closed',
  status: 'Status',
  openingCash: 'Opening cash',
  cardSales: 'Card',
  creditSales: 'Credit',
  totalOrders: 'Orders',
  cashExpenses: 'Cash expenses',
  cashPurchases: 'Cash purchases',
  expectedCash: 'Expected cash',
  actualCash: 'Counted cash',
  kotSent: 'KOT sent',
  kotVoided: 'KOT voided',
};

export function ShiftsPage() {
  const { currency, filters, multiBranch, multiDevice } = usePortal();
  const range = useMemo(() => toReportRange(filters.filters), [filters.filters]);
  const toolbar = useReportToolbar();
  const layout = useReportLayout('shifts', TOGGLEABLE);

  const shiftsQuery = useQuery({
    queryKey: portalKeys.shifts(range),
    queryFn: ({ signal }) => fetchShifts(range, signal),
  });

  const rows = useMemo(
    () =>
      applyClientFilters(shiftsQuery.data ?? [], {
        search: toolbar.search,
        filterRows: toolbar.filterRows,
        matchAny: toolbar.matchAny,
        fieldValue: (row, field) =>
          field === 'Opened by' ? row.openedBy ?? undefined : field === 'Status' ? row.status : undefined,
        searchValues: (row) => [row.openedBy, row.branchName, row.deviceName, row.status],
      }),
    [shiftsQuery.data, toolbar.search, toolbar.filterRows, toolbar.matchAny],
  );

  const baseColumns = useMemo<Array<Column<ShiftReportRow>>>(() => {
    const money = (value: string | null) => formatMoney(value, currency);
    const list: Array<Column<ShiftReportRow>> = [
      {
        key: 'opened',
        header: 'Opened',
        pinned: true,
        cell: (row) => (
          <div>
            <p className="whitespace-nowrap font-medium text-slate-900">{formatPosDateTime(row.openTime)}</p>
            <p className="mt-0.5 text-xs text-slate-400">
              {row.openedBy || '—'}
              {multiBranch && row.branchName ? ` · ${row.branchName}` : ''}
              {multiDevice && row.deviceName ? ` · ${row.deviceName}` : ''}
            </p>
          </div>
        ),
      },
      {
        key: 'closed',
        header: 'Closed',
        cell: (row) => (
          <span className="whitespace-nowrap text-slate-500">
            {row.closeTime === null ? '—' : formatPosDateTime(row.closeTime)}
          </span>
        ),
      },
      { key: 'status', header: 'Status', cell: (row) => <StatusBadge status={row.status} /> },
      {
        key: 'openingCash',
        header: 'Opening cash',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) => money(row.openingCash),
      },
      {
        key: 'cashSales',
        header: 'Cash',
        align: 'right',
        pinned: true,
        cellClassName: 'tabular-nums',
        cell: (row) => money(row.cashSales),
      },
      {
        key: 'cardSales',
        header: 'Card',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) => money(row.cardSales),
      },
      {
        key: 'creditSales',
        header: 'Credit',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) => money(row.creditSales),
      },
      {
        key: 'totalSales',
        header: 'Total sales',
        align: 'right',
        pinned: true,
        cellClassName: 'tabular-nums font-medium text-slate-900',
        cell: (row) => money(row.totalSales),
      },
      {
        key: 'totalOrders',
        header: 'Orders',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) => formatCount(row.totalOrders),
      },
      {
        key: 'cashExpenses',
        header: 'Cash expenses',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) => money(row.cashExpenses),
      },
      {
        key: 'cashPurchases',
        header: 'Cash purchases',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) => money(row.cashPurchases),
      },
      {
        key: 'expectedCash',
        header: 'Expected cash',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) => money(row.expectedCash),
      },
      {
        key: 'actualCash',
        header: 'Counted cash',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) => money(row.actualCash),
      },
      {
        key: 'variance',
        header: 'Variance',
        align: 'right',
        pinned: true,
        cell: (row) => <Variance value={row.variance} currency={currency} />,
      },
      {
        key: 'kotSent',
        header: 'KOT sent',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) => formatCount(row.kotSent),
      },
      {
        key: 'kotVoided',
        header: 'KOT voided',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) => formatCount(row.kotVoided),
      },
    ];
    return list;
  }, [currency, multiBranch, multiDevice]);

  const columns = useMemo(() => applyReportLayout(baseColumns, layout), [baseColumns, layout]);

  const shortCount = rows.filter((row) => amountSign(row.variance) < 0).length;

  return (
    <>
      <PageHeader
        title="Shift reports"
        description="Every till session in the range, with the cash it opened and closed on. A negative variance means the drawer came up short."
        actions={
          <ExportMenu
            report="shifts"
            range={range}
            // Nothing to export until the report has loaded, and an
            // errored report would produce an empty file.
            disabled={shiftsQuery.isPending || shiftsQuery.isError}
          />
        }
      />

      <SectionCard
        headingId="shifts-table"
        title="Shifts"
        count={rows.length}
        description={
          shortCount > 0
            ? `${shortCount} shift${shortCount === 1 ? '' : 's'} closed short in this range.`
            : 'Scroll sideways inside the table for the full cash breakdown.'
        }
      >
        <ReportToolbar>
          <ReportSearchField
            value={toolbar.search}
            onChange={toolbar.setSearch}
            placeholder="Staff or status"
          />
          <ReportFiltersButton
            fields={FILTER_FIELDS}
            applied={toolbar.filterRows}
            matchAny={toolbar.matchAny}
            onApply={toolbar.applyFilters}
            placeholderFor={(field) => (field === 'Status' ? 'Open / Closed' : 'name')}
          />
          <ReportColumnsButton layout={layout} labels={COLUMN_LABELS} />
        </ReportToolbar>
        <AsyncSection
          state={shiftsQuery}
          skeleton={<TableSkeleton rows={5} columns={6} />}
          isEmpty={() => rows.length === 0}
          emptyIcon={<IconClock className="h-5 w-5" />}
          emptyTitle={toolbar.search ? `No shifts match “${toolbar.search}”` : 'No shifts in this range'}
          emptyDescription="Pick a wider date range, or check another branch."
        >
          {() => (
            <DataTable
              caption="Shift reports with opening cash, sales split and cash variance"
              columns={columns}
              rows={rows}
              rowKey={(row) => row.id}
              rowClassName={(row) => (amountSign(row.variance) < 0 ? 'bg-rose-50/40' : undefined)}
              layout={layout}
            />
          )}
        </AsyncSection>
      </SectionCard>
    </>
  );
}
